import { Box } from '@chakra-ui/react';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
  memo,
} from 'react';
import { useShowToast } from '@/hooks';
import { FormBuilder } from '@/components/formBuilder';
import { FormItem } from '@/components/formBuilder/components';
import { SectionWithHeading } from '@/ui/SectionHeading';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { TemplateEmailComposer } from '@/components/ats/EmailTemplates/TemplateEmailComposer';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useLazyGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { checkForInvalidVariablesInSubjectAndBody } from '@/utils/commonHelpers';
import {
  INTEGRATION_TYPES,
  RADIO_OPTIONS,
  TEMPLATE_VARIABLES_VIEW,
  ESIGN_EDITOR_PLUGINS,
  ESIGN_SUBJECT_MAX_LENGTH,
  ESIGN_MESSAGES,
  ESIGN_SECTION_TITLES,
  ESIGN_RECIPIENT_FIELDS,
  ESIGN_CUSTOM_MESSAGE_TOGGLE,
  USE_TEXT_EDITOR,
  ESIGN_SECTION_ANCHOR_ATTR,
  ESIGN_SECTION_TITLE_ATTR,
} from '@/constants/esignature';
import { scrollToInvalidVariablesAlert } from '@/utils/esignature';
import type { DefaultEsignEnvelope } from '@/types/esignature';
import { useOfferStore } from '../../store';
import { useGetDefaultESignQuery } from '@/store/query/e-signs';
import { toVariablesList } from '@/utils/emailComposition';

// DocuSign per-recipient composer field names (candidate/shared uses useEmailField's default subject/body).
const COMPANY_EMAIL_SUBJECT_FIELD = 'company_email_subject';
const COMPANY_EMAIL_BODY_FIELD = 'company_email_body';
const CC_EMAIL_SUBJECT_FIELD = 'cc_email_subject';
const CC_EMAIL_BODY_FIELD = 'cc_email_body';
const CUSTOM_MESSAGE_FIELD = 'custom_message_per_recipient';
const CC_USER_IDS_FIELD = 'cc_user_ids';

export interface EsignEmailFormRef {
  getModel: () => Promise<{
    hasValidData: boolean;
    payload: any;
    rawValues?: {
      company_signer_required: boolean;
      signing_order: string;
      email_subject: string;
      email_body: string;
      company_signer_id?: string;
      custom_message_per_recipient?: boolean;
      company_email_subject?: string;
      company_email_body?: string;
      cc_email_subject?: string;
      cc_email_body?: string;
      cc_user_ids?: number[];
    };
  }>;
}

export interface EsignEmailFormProps {
  integrationType: 'docusign' | 'pandadocs' | 'adobe' | 'spotdraft';
  isAdmin?: boolean;
  jobId?: string | number;
  /**
   * Seed the form from a caller that already holds the envelope config, instead
   * of the account default at GET /esign_envelopes/default.
   *
   * The offer flows leave this unset and keep reading the account default. The
   * e-sign stage activity passes its own persisted config, which lives on the
   * stage action rather than on the account — without this the composer would
   * always reopen showing account defaults over the recruiter's saved values.
   * Supplying it also skips the default fetch entirely.
   */
  initialValues?: DefaultEsignEnvelope;
}

const getIntegrationTypeParam = (integrationType: string) => {
  if (integrationType === INTEGRATION_TYPES.PANDADOC) {
    return `${integrationType}/pandadocs_api`;
  }
  if (integrationType === INTEGRATION_TYPES.ADOBE) {
    return `${integrationType}/adobe_api`;
  }
  if (integrationType === INTEGRATION_TYPES.DOCUSIGN) {
    return `${integrationType}/docusign_api`;
  }
  if (integrationType === INTEGRATION_TYPES.SPOTDRAFT) {
    return `${integrationType}/spotdraft_api`;
  }
  return undefined;
};

const EsignEmailFormComponent = forwardRef<
  EsignEmailFormRef,
  EsignEmailFormProps
>((props, comRef) => {
  // Destructured in the body rather than the parameter list: adding a fourth
  // prop pushed the inline destructure past the print width, and letting it
  // wrap would reindent this entire component for a one-line change.
  const {
    integrationType,
    isAdmin = false,
    jobId: jobIdProp,
    initialValues,
  } = props;
  const formRef = useRef<any>(null);
  const toast = useShowToast();
  const applicationData = useOfferStore((state) => state.applicationData);

  const integrationTypeParam = useMemo(
    () => getIntegrationTypeParam(integrationType),
    [integrationType]
  );

  const jobId = jobIdProp ?? applicationData?.job_id;
  const queryParams = integrationTypeParam
    ? { integration_type: integrationTypeParam }
    : {};
  const jobIdParam = jobId ? { job_id: jobId } : {};

  const { data, isFetching } = useGetDefaultESignQuery(
    {
      ...queryParams,
      ...jobIdParam,
    },
    { skip: Boolean(initialValues) }
  );

  const [getVariables, { data: { variables = [] } = {} }] =
    useLazyGetTemplatesVariablesQuery();

  const usersList = useSelector(selectUsers);

  const variablesListArr = useMemo(
    () => toVariablesList(variables),
    [variables]
  );

  const {
    company_signer_required,
    signing_order,
    company_signer,
    email_body,
    email_subject,
    custom_message_per_recipient,
    company_email_subject,
    company_email_body,
    cc_email_subject,
    cc_email_body,
    cc_user_ids,
  } = (initialValues || data || {}) as DefaultEsignEnvelope;

  // Per-recipient model (toggle, company/carbon-copy emails, CC) is DocuSign-only.
  const isDocusign = integrationType === INTEGRATION_TYPES.DOCUSIGN;

  useEffect(() => {
    getVariables({ params: { view: TEMPLATE_VARIABLES_VIEW } });
  }, [getVariables]);

  const useTextEditor = useMemo(() => {
    return USE_TEXT_EDITOR[integrationType] ?? USE_TEXT_EDITOR.docusign;
  }, [integrationType]);

  // Widened: the `as const` maps narrow the empty entries to `never[]`.
  const pluginsConfig = useMemo((): readonly string[] => {
    if (integrationType in ESIGN_EDITOR_PLUGINS) {
      return ESIGN_EDITOR_PLUGINS[integrationType];
    }
    return ESIGN_EDITOR_PLUGINS.docusign;
  }, [integrationType]);

  const subjectMaxLength = useMemo(() => {
    return (
      ESIGN_SUBJECT_MAX_LENGTH[integrationType] ||
      ESIGN_SUBJECT_MAX_LENGTH.docusign
    );
  }, [integrationType]);

  // One composer per recipient; no sender row (e-sign sends from the integration).
  const baseComposerProps = {
    showFrom: false,
    variables,
    subjectMaxLength,
    hideToolbar: !useTextEditor,
    enableFontControls: false,
    // Same per-integration link/image gating, expressed as toolbar features.
    toolbarFeatures: {
      link: pluginsConfig.includes('link'),
      image: pluginsConfig.includes('image'),
      attachment: false,
    },
  };

  const composerField = (
    id: string,
    subjectFieldName?: string,
    bodyFieldName?: string
  ) => [
    {
      id,
      type: 'custom',
      Field: TemplateEmailComposer,
      props: { ...baseComposerProps, subjectFieldName, bodyFieldName },
    },
  ];

  const rawEmailFieldConfigs = composerField('esignEmailComposer');

  const companyEmailFieldConfigs = composerField(
    'esignCompanyEmailComposer',
    COMPANY_EMAIL_SUBJECT_FIELD,
    COMPANY_EMAIL_BODY_FIELD
  );

  const carbonCopyEmailFieldConfigs = composerField(
    'esignCarbonCopyEmailComposer',
    CC_EMAIL_SUBJECT_FIELD,
    CC_EMAIL_BODY_FIELD
  );

  const isSpotDraft = integrationType === INTEGRATION_TYPES.SPOTDRAFT;

  // SpotDraft sends with no body at all — no message, no subject/contract
  // name, no company signer — the backend derives everything from the
  // account's default template. This step (reached only via the
  // document_generation_skipped wizard) has nothing left to collect.
  const emailFieldConfigs = useMemo(() => {
    if (!isSpotDraft) return rawEmailFieldConfigs;
    return [];
  }, [rawEmailFieldConfigs, isSpotDraft]);

  useImperativeHandle(comRef, () => ({
    getModel: async () => {
      // SpotDraft sends with no body — the backend derives the contract name
      // from the account's default template — so there's nothing to collect
      // or validate here (this step only still exists for
      // document_generation_skipped offers, which need the wizard's template
      // step first; the "signature flow" step itself is just a confirm now).
      if (isSpotDraft) {
        return { hasValidData: true, payload: {} };
      }

      if (!formRef.current) {
        return {
          hasValidData: false,
          payload: {},
        };
      }

      const form = formRef.current;
      const { values } = form || {};
      const {
        subject,
        body,
        company_signer_id,
        company_signer_required,
        signing_order,
        [CUSTOM_MESSAGE_FIELD]: customMessagePerRecipient,
        [COMPANY_EMAIL_SUBJECT_FIELD]: companyEmailSubject,
        [COMPANY_EMAIL_BODY_FIELD]: companyEmailBody,
        [CC_EMAIL_SUBJECT_FIELD]: ccEmailSubject,
        [CC_EMAIL_BODY_FIELD]: ccEmailBody,
        [CC_USER_IDS_FIELD]: ccUserIds,
      } = values || {};

      const ccUserIdsList = Array.isArray(ccUserIds) ? ccUserIds : [];
      const employerSigns = company_signer_required === '1';
      // Per-recipient blocks are required + sent only when their recipient is present (DocuSign ON mode).
      const customPerRecipient = isDocusign && !!customMessagePerRecipient;
      const showCompanyEmail = customPerRecipient && employerSigns;
      const showCarbonCopyEmail =
        customPerRecipient && ccUserIdsList.length > 0;

      const subjectVariables = variablesListArr.filter(
        (variable) => variable !== 'sender.email_signature'
      );
      const hasInvalidVariables = (subjectValue: string, bodyValue: string) =>
        checkForInvalidVariablesInSubjectAndBody({
          variablesListArr,
          body: bodyValue,
          subject: subjectValue,
          variablesListSubject: subjectVariables,
        });

      // Candidate/shared subject is always required.
      if (!subject) {
        toast({
          title: isSpotDraft
            ? 'Contract name is required'
            : ESIGN_MESSAGES.SUBJECT_REQUIRED,
          status: 'error',
        });
        return {
          hasValidData: false,
          payload: {},
        };
      }

      if (hasInvalidVariables(subject, body)) {
        scrollToInvalidVariablesAlert();
        return {
          hasValidData: false,
          payload: {},
        };
      }

      // Company block (ON mode + an employer signs): subject required + tokens.
      if (showCompanyEmail) {
        if (!companyEmailSubject) {
          toast({
            title: ESIGN_MESSAGES.COMPANY_SUBJECT_REQUIRED,
            status: 'error',
          });
          return { hasValidData: false, payload: {} };
        }
        if (hasInvalidVariables(companyEmailSubject, companyEmailBody)) {
          toast({
            title: ESIGN_MESSAGES.COMPANY_INVALID_VARIABLES,
            status: 'error',
          });
          return { hasValidData: false, payload: {} };
        }
      }

      // Carbon-copy block (ON mode + CC recipients present): subject + tokens.
      if (showCarbonCopyEmail) {
        if (!ccEmailSubject) {
          toast({
            title: ESIGN_MESSAGES.CARBON_COPY_SUBJECT_REQUIRED,
            status: 'error',
          });
          return { hasValidData: false, payload: {} };
        }
        if (hasInvalidVariables(ccEmailSubject, ccEmailBody)) {
          toast({
            title: ESIGN_MESSAGES.CARBON_COPY_INVALID_VARIABLES,
            status: 'error',
          });
          return { hasValidData: false, payload: {} };
        }
      }

      const resolvedSigningOrder =
        signing_order === '1' ? 'company_signer_first' : 'candidate_first';

      const payload: any = {
        esign_envelope: {
          email_subject: subject || '',
          email_body: body || '',
        },
      };

      if (employerSigns && company_signer_id) {
        payload.esign_envelope.company_signer_id = company_signer_id;
      }
      if (employerSigns && signing_order !== undefined) {
        payload.esign_envelope.signing_order = resolvedSigningOrder;
      }
      // Toggle + CC recipients are always sent for DocuSign (CC defaults to []).
      if (isDocusign) {
        payload.esign_envelope.custom_message_per_recipient =
          customPerRecipient;
        payload.esign_envelope.cc_user_ids = ccUserIdsList;
      }
      if (showCompanyEmail) {
        payload.esign_envelope.company_email_subject =
          companyEmailSubject || '';
        payload.esign_envelope.company_email_body = companyEmailBody || '';
      }
      if (showCarbonCopyEmail) {
        payload.esign_envelope.cc_email_subject = ccEmailSubject || '';
        payload.esign_envelope.cc_email_body = ccEmailBody || '';
      }

      return {
        hasValidData: true,
        payload,
        rawValues: {
          company_signer_required: employerSigns,
          signing_order: resolvedSigningOrder,
          email_subject: subject || '',
          email_body: body || '',
          ...(employerSigns && company_signer_id ? { company_signer_id } : {}),
          ...(isDocusign
            ? {
                custom_message_per_recipient: customPerRecipient,
                cc_user_ids: ccUserIdsList,
              }
            : {}),
          ...(showCompanyEmail
            ? {
                company_email_subject: companyEmailSubject || '',
                company_email_body: companyEmailBody || '',
              }
            : {}),
          ...(showCarbonCopyEmail
            ? {
                cc_email_subject: ccEmailSubject || '',
                cc_email_body: ccEmailBody || '',
              }
            : {}),
        },
      };
    },
  }));

  const createSigningDetailsFields = () => [
    isAdmin && {
      name: 'company_signer_required',
      label: 'Does anyone from the employer need to sign?',
      styles: { spacing: '3rem' },
      type: 'radio2',
      radioStyles: { variant: 'ats-primary-sm' },
      options: RADIO_OPTIONS.YES_NO,
    },
    {
      name: 'company_signer_id',
      label: isAdmin
        ? 'Choose who signs on behalf of the employer'
        : 'Company signer',
      isRequired: true,
      type: 'select',
      options: usersList,
      customFormatLabel: SelectOptionWithAvatar,
      valueKey: 'id',
      labelKey: 'name',
      isConditional: true,
      conditionConfig: {
        type: 'and',
        conditions: [{ field: 'company_signer_required', value: '1' }],
      },
    },
    {
      name: 'signing_order',
      label: 'Order of signing',
      radioStyles: { variant: 'ats-primary-sm' },
      styles: { spacing: '3rem' },
      type: 'radio2',
      isConditional: true,
      disabled: !isAdmin,
      conditionConfig: {
        type: 'and',
        conditions: [{ field: 'company_signer_required', value: '1' }],
      },
      options: RADIO_OPTIONS.SIGNING_ORDER,
    },
  ];

  // CC recipients (DocuSign) — a Kula-user multi-select storing a user-id array.
  // Its own section rather than a trailing field in "E-sign details": who
  // receives a copy is a different question from who signs and in what order,
  // and the heading is what makes that read as a separate decision.
  const ccRecipientsField = {
    id: 'ccRecipients',
    name: CC_USER_IDS_FIELD,
    label: ESIGN_RECIPIENT_FIELDS.CC.LABEL,
    placeholder: ESIGN_RECIPIENT_FIELDS.CC.PLACEHOLDER,
    type: 'select',
    isMulti: true,
    isClearable: true,
    options: usersList,
    customFormatLabel: SelectOptionWithAvatar,
    valueKey: 'id',
    labelKey: 'name',
  };

  // Per-recipient toggle (DocuSign): OFF → one shared email; ON → a subject/body per recipient.
  const customMessageToggleField = {
    id: 'customMessageToggle',
    name: CUSTOM_MESSAGE_FIELD,
    label: ESIGN_CUSTOM_MESSAGE_TOGGLE.LABEL,
    subLabel: ESIGN_CUSTOM_MESSAGE_TOGGLE.HINT,
    type: 'switch',
    // Drop the default 1em top margin so it sits close to the Details section above.
    containerStyles: { margin: '0 0 16px' },
  };

  const offerSectionProps = {
    background: 'secondary-50',
    containerStyles: {
      mb: '16px',
      padding: '5px',
    },
    contentStyles: {
      bg: 'white',
      borderRadius: '0 0 6px 6px',
    },
    textProps: {
      fontSize: '26px',
    },
    headingProps: {
      mb: '16px',
    },
  };

  const sectionAnchor = (anchorId: string, title: string) => ({
    [ESIGN_SECTION_ANCHOR_ATTR]: anchorId,
    [ESIGN_SECTION_TITLE_ATTR]: title,
  });

  const renderComposerSection = (
    title: string,
    configs: Array<Record<string, any>>,
    keyPrefix: string,
    formik: any
  ) => (
    <SectionWithHeading
      title={title}
      {...offerSectionProps}
      containerStyles={{
        ...offerSectionProps.containerStyles,
        ...sectionAnchor(keyPrefix, title),
      }}
    >
      {configs.map((field, index) => (
        <FormItem key={`${keyPrefix}-${index}`} field={field} formik={formik} />
      ))}
    </SectionWithHeading>
  );

  // DocuSign composers as one reactive item: OFF → shared "Email", ON → per-recipient blocks (candidate stays mounted across the toggle).
  const docusignMessagesSection = isDocusign && {
    id: 'docusignMessages',
    type: 'custom',
    // Metadata for config introspection/tests — the Field renders these itself.
    fields: {
      candidate: emailFieldConfigs,
      company: companyEmailFieldConfigs,
      carbonCopy: carbonCopyEmailFieldConfigs,
    },
    Field: ({ formik }: { formik?: any }) => {
      const values = formik?.values || {};
      const customPerRecipient = !!values[CUSTOM_MESSAGE_FIELD];
      const employerSigns = values.company_signer_required === '1';
      const ccUsers = values[CC_USER_IDS_FIELD];
      const hasCcUsers = Array.isArray(ccUsers) && ccUsers.length > 0;

      // Company + carbon-copy stay mounted (just hidden) so their content survives
      // toggling off/on — the field container clears a field's value on unmount.
      return (
        <>
          {renderComposerSection(
            customPerRecipient
              ? ESIGN_SECTION_TITLES.CANDIDATE_EMAIL
              : ESIGN_SECTION_TITLES.EMAIL,
            emailFieldConfigs,
            'candidate',
            formik
          )}
          <Box hidden={!(customPerRecipient && employerSigns)}>
            {renderComposerSection(
              ESIGN_SECTION_TITLES.COMPANY_EMAIL,
              companyEmailFieldConfigs,
              'company',
              formik
            )}
          </Box>
          <Box hidden={!(customPerRecipient && hasCcUsers)}>
            {renderComposerSection(
              ESIGN_SECTION_TITLES.CARBON_COPY_EMAIL,
              carbonCopyEmailFieldConfigs,
              'carbonCopy',
              formik
            )}
          </Box>
        </>
      );
    },
  };

  const formConfig = [
    // Details is about signing: shown only when a company signer is relevant.
    // It previously also rendered for DocuSign regardless, because it carried
    // the CC picker — now that CC has its own section, that clause would leave
    // an empty heading whenever no signer is involved.
    !isSpotDraft &&
      (company_signer_required || isAdmin) && {
        id: 'signingDetails',
        type: 'wrapper',
        Wrapper: SectionWithHeading,
        wrapperProps: {
          title: ESIGN_SECTION_TITLES.DETAILS,
          ...offerSectionProps,
          containerStyles: {
            ...offerSectionProps.containerStyles,
            ...sectionAnchor('details', ESIGN_SECTION_TITLES.DETAILS),
          },
        },
        fields: createSigningDetailsFields(),
      },
    !isSpotDraft &&
      isDocusign && {
        id: 'copyRecipients',
        type: 'wrapper',
        Wrapper: SectionWithHeading,
        wrapperProps: {
          title: ESIGN_SECTION_TITLES.COPY_RECIPIENTS,
          ...offerSectionProps,
          containerStyles: {
            ...offerSectionProps.containerStyles,
            ...sectionAnchor(
              'copyRecipients',
              ESIGN_SECTION_TITLES.COPY_RECIPIENTS
            ),
          },
        },
        fields: [ccRecipientsField],
      },
    // DocuSign: per-recipient toggle → reactive composers.
    isDocusign && customMessageToggleField,
    docusignMessagesSection,
    // Other providers keep the single "Message" composer (no toggle/CC).
    !isSpotDraft &&
      !isDocusign && {
        id: 'emailDetails',
        type: 'wrapper',
        Wrapper: SectionWithHeading,
        wrapperProps: {
          title: ESIGN_SECTION_TITLES.MESSAGE,
          ...offerSectionProps,
        },
        fields: [...emailFieldConfigs],
      },
  ].filter(Boolean);

  // Stable identity so Formik's enableReinitialize doesn't reset the editor on every re-render.
  const formInitialValues = useMemo(
    () => ({
      body: email_body,
      subject: email_subject,
      company_signer_id: company_signer?.id,
      company_signer_required: company_signer_required ? '1' : '0',
      signing_order: signing_order === 'company_signer_first' ? '1' : '0',
      [CUSTOM_MESSAGE_FIELD]: !!custom_message_per_recipient,
      [COMPANY_EMAIL_SUBJECT_FIELD]: company_email_subject || '',
      [COMPANY_EMAIL_BODY_FIELD]: company_email_body || '',
      [CC_EMAIL_SUBJECT_FIELD]: cc_email_subject || '',
      [CC_EMAIL_BODY_FIELD]: cc_email_body || '',
      [CC_USER_IDS_FIELD]: cc_user_ids || [],
    }),
    [
      email_body,
      email_subject,
      company_signer?.id,
      company_signer_required,
      signing_order,
      custom_message_per_recipient,
      company_email_subject,
      company_email_body,
      cc_email_subject,
      cc_email_body,
      cc_user_ids,
    ]
  );

  if (!initialValues && isFetching) {
    return null;
  }

  return (
    <Box w="100%">
      <FormBuilder
        initialValues={formInitialValues}
        config={formConfig}
        onSubmit={() => {}}
        formRef={formRef}
      />
    </Box>
  );
});

EsignEmailFormComponent.displayName = 'EsignEmailForm';

// Memoised so the parent's per-poll re-renders don't re-initialise the editor (props are primitive).
export const EsignEmailForm = memo(EsignEmailFormComponent);
